import { format } from 'date-fns';
import { useId, useMemo } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { CountryFlagWithLabel } from '@/components/CountryFlag';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import Input from '@/components/Input/Input';
import { InputPostalCode } from '@/components/Input/PostalCode/InputPostalCode';
import { HStack, VStack } from '@/components/Layout';
import SelectCountry from '@/components/Select/SelectCountry';
import SelectStateProvince from '@/components/Select/SelectStateProvince';
import { useStateProvinces } from '@/components/Select/useStateProvinces';
import { Text } from '@/components/Typography/Text';
import { type SupportedCountry, isSupportedCountry } from '@/constants/country';
import useFormatValidDuration from '@/features/person/hooks/useFormatValidDuration';
import type { HometownValue } from '@/features/section-attribute/types';
import type { Preset } from '@/hooks/date';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = CFormItemProps & {
  value?: HometownValue[];
  onChange: (value: HometownValue[]) => void;
};
function useStateProvinceLabelMap(items: HometownValue[]): Map<string, string> {
  const uniqueCountries = useMemo(() => {
    const codes = new Set<string>();
    for (const item of items) {
      if (item.countryCode && isSupportedCountry(item.countryCode)) {
        codes.add(item.countryCode);
      }
    }
    return [...codes];
  }, [items]);
  const firstCountry = uniqueCountries[0] as SupportedCountry | undefined;
  const {
    stateProvinces
  } = useStateProvinces(firstCountry);
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const sp of stateProvinces) {
      map.set(sp.value, sp.label);
    }
    return map;
  }, [stateProvinces]);
}
function HometownReadCard({
  item,
  stateProvinceLabelMap,
  formatDateRange
}: {
  item: HometownValue;
  stateProvinceLabelMap: Map<string, string>;
  formatDateRange: (opts: {
    validFrom: string;
    validTo?: string;
    formatStr?: keyof Preset;
    isShort?: boolean;
  }) => string;
}) {
  const stateLabel = stateProvinceLabelMap.get(item.stateProvinceCode ?? '') ?? item.stateProvinceCode ?? '';
  const mainValue = item.countryCode === 'JP' ? [stateLabel, item.city].filter(Boolean).join(' ') : stateLabel;
  const subValues = item.countryCode === 'JP' ? [item.additionalAreaInfo, item.detailInformation] : [item.detailInformation, item.additionalAreaInfo, item.city];
  const dateRangeText = item.durationFrom ? formatDateRange({
    validFrom: item.durationFrom,
    validTo: item.durationTo ?? undefined,
    formatStr: 'yearMonth',
    isShort: true
  }) : undefined;
  const captionParts = [...subValues, item.postalCode].filter(Boolean).join(', ');
  return <VStack className="gap-1">
      {item.countryCode && <CountryFlagWithLabel countryCode={item.countryCode} label={<Text variant="labelSmall" className="text-neutral-muted-on-fill">
              {item.countryCode}
            </Text>} />}
      {mainValue && <Text variant="labelLarge" className="text-neutral-on-fill">
          {mainValue}
        </Text>}
      {captionParts && <Text variant="label" className="text-neutral-muted-on-fill">
          {captionParts}
        </Text>}
      {dateRangeText && <Text variant="label" className="text-neutral-muted-on-fill">
          {dateRangeText}
        </Text>}
    </VStack>;
}

/**
 * 出身地リストの入力を行う CFormItem
 *
 * 各項目: countryCode, stateProvinceCode, city, postalCode,
 * additionalAreaInfo, detailInformation, durationFrom, durationTo
 */
export const CFormHometownListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.hometownItem'
  });
  const {
    dateRange: formatDateRange
  } = useFormatValidDuration();
  const idPrefix = useId();
  const values = props.value ?? [];
  const stateProvinceLabelMap = useStateProvinceLabelMap(values);
  const handleAdd = () => {
    props.onChange([...values, {
      countryCode: '',
      stateProvinceCode: '',
      stateProvinceName: '',
      city: '',
      postalCode: '',
      additionalAreaInfo: '',
      detailInformation: '',
      durationFrom: '',
      durationTo: ''
    }]);
  };
  const handleRemove = (index: number) => {
    props.onChange(values.filter((_, i) => i !== index));
  };
  const updateItem = (index: number, patch: Partial<HometownValue>) => {
    const next = [...values];
    next[index] = {
      ...next[index],
      ...patch
    };
    props.onChange(next);
  };
  const renderReadMode = () => {
    if (values.length === 0) {
      return <Text variant="pSmall" className="text-neutral-muted-on-fill">
          -
        </Text>;
    }
    return <VStack className="gap-4 w-full">
        {values.map((item, index) => <HometownReadCard key={`${idPrefix}-read-${index}`} item={item} stateProvinceLabelMap={stateProvinceLabelMap} formatDateRange={formatDateRange} />)}
      </VStack>;
  };
  const renderWriteMode = () => {
    return <VStack className="gap-4 w-full">
        {values.map((item, idx) => <VStack key={`${idPrefix}-write-${idx}`} className="gap-3 border-l-2 border-neutral-muted-border pl-4 py-2 w-full">
            <HStack className="justify-between items-start">
              <Text variant="label" className="text-neutral-muted-on-fill">
                {t('hometown')} {idx + 1}
              </Text>
              {!props.readonly && <ActionButton icon="Trash2Icon" variant="ghost" iconColor="muted" onClick={() => handleRemove(idx)} aria-label={tc('action.delete')} />}
            </HStack>

            <HStack className="gap-2">
              <VStack className="flex-1 gap-1">
                <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                  {t('country')}
                </Text>
                <SelectCountry className="w-full" value={item.countryCode && isSupportedCountry(item.countryCode) ? item.countryCode as SupportedCountry : undefined} onChange={val => updateItem(idx, {
              countryCode: val ?? '',
              stateProvinceCode: ''
            })} disabled={props.readonly} />
              </VStack>
              <VStack className="flex-1 gap-1">
                <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                  {t('stateProvince')}
                </Text>
                <SelectStateProvince rootClassName="w-full" country={item.countryCode && isSupportedCountry(item.countryCode) ? item.countryCode as SupportedCountry : undefined} value={item.stateProvinceCode ?? ''} onValueChange={val => updateItem(idx, {
              stateProvinceCode: val
            })} disabled={props.readonly} />
              </VStack>
            </HStack>

            <VStack className="gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('postalCode')}
              </Text>
              <InputPostalCode countryCode={item.countryCode && isSupportedCountry(item.countryCode) ? item.countryCode as SupportedCountry : undefined} value={item.postalCode ?? ''} onValueChange={val => updateItem(idx, {
            postalCode: val
          })} disabled={props.readonly} />
            </VStack>

            <VStack className="flex-1 gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('city')}
              </Text>
              <Input width="100%" value={item.city ?? ''} onChange={e => updateItem(idx, {
            city: e.target.value
          })} disabled={props.readonly} />
            </VStack>

            <VStack className="gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('additionalAreaInfo')}
              </Text>
              <Input width="100%" value={item.additionalAreaInfo ?? ''} onChange={e => updateItem(idx, {
            additionalAreaInfo: e.target.value
          })} disabled={props.readonly} />
            </VStack>

            <VStack className="gap-1">
              <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                {t('detailInformation')}
              </Text>
              <Input width="100%" value={item.detailInformation ?? ''} onChange={e => updateItem(idx, {
            detailInformation: e.target.value
          })} disabled={props.readonly} />
            </VStack>

            <HStack className="gap-2">
              <VStack className="flex-1 gap-1">
                <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                  {t('durationFrom')}
                </Text>
                <SimpleDatePicker value={item.durationFrom ? new Date(item.durationFrom) : undefined} onChange={val => updateItem(idx, {
              durationFrom: val ? format(val, 'yyyy-MM-dd') : ''
            })} disabled={props.readonly} width="100%" allowEmpty />
              </VStack>
              <VStack className="flex-1 gap-1">
                <Text variant="labelSmall" className="text-neutral-muted-on-fill">
                  {t('durationTo')}
                </Text>
                <SimpleDatePicker value={item.durationTo ? new Date(item.durationTo) : undefined} onChange={val => updateItem(idx, {
              durationTo: val ? format(val, 'yyyy-MM-dd') : ''
            })} disabled={props.readonly} width="100%" allowEmpty />
              </VStack>
            </HStack>
          </VStack>)}

        {!props.readonly && <ActionButton className="pl-0 self-start" icon="PlusIcon" onClick={handleAdd} variant="ghost" iconColor="primary">
            {tc('action.add')}
          </ActionButton>}
      </VStack>;
  };
  return <CFormItemBase {...props} readComponent={renderReadMode()} writeComponent={renderWriteMode()} />;
});